// @flow
import React from 'react';
import {
    Box,
    Stack,
    Columns,
    Column,
    Text,
    Flex,
    Position,
    Icon as NeoIcon,
    Button as NeoButton
} from '@locus-taxy/neo-ui';
import Tether from 'tether';
import TetherComponent from 'react-tether';
import { type TFunction } from 'react-i18next';

import theme from './FpPlanViewActionsBar.scss';

import Divider from 'common/components/common/Divider';
import { DivWithPopover } from 'common/components/core/wrappers/Div';

import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import FpPlanViewPagination from './FpPlanViewPagination';
import { neoUIColors } from 'common/enums/ColorEnum';
import FpActionsBarPopup from './FpActionsBarPopup';

import FpBeatFilters from './FpBeatFilters';
import FpBeatSort from './FpBeatSort';
import { EnhancedInput } from 'common/components/core/input';
import { FpContainers } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';
import { type FpBeat } from 'common/models/internal/FpBeat';
import { type FpPjPlan } from 'common/models/internal/FpPjPlan';
import { type SalesCategory } from 'common/models/internal/SalesCategory';

type State = {
    selectedOption: string,
    optionsVisible: boolean,
    searchValue: ?string,
    filterPopupOpen: boolean,
    sortPopupOpen: boolean
};

type Props = {
    changeVisitsView: (view: string) => void,
    getContainer: () => string,
    onSearchInputChange: () => void,
    filters: Array<string>,
    beats: Array<FpBeat>,
    pjPlan: FpPjPlan,
    applyFilters: () => void,
    paginationDetails: FpPlanViewPagination,
    onPageChange: () => void,
    onPageSizeChange: () => void,
    sortBeats: () => void,
    resetSort: () => void,
    selectedSortOption: string,
    selectedSortOrder: string,
    outletSalesCategories: Array<SalesCategory>,
    onClearFilter: () => void,
    selectedFilters: Array<string>,
    getAllFilters: () => Array<string>,
    t: TFunction
};

export default class FpPlanViewActionsBar extends React.Component<
    Props,
    State
> {
    popupRef: React.RefObject;

    constructor(props) {
        super(props);
        this.state = {
            selectedOption: props.selectedVisitsView || FpContainers.Allocated,
            optionsVisible: false,
            filterPopupOpen: false,
            sortPopupOpen: false,
            searchValue: null
        };
    }

    getPopupRef = ref => {
        if (!ref) {
            return null;
        }
        this.popupRef = ref;
    };

    getPopoverOptions = () => {
        const { selectedOption } = this.state;
        const { changeVisitsView, getContainer, t } = this.props;
        return (
            <FpActionsBarPopup
                t={t}
                getContainer={getContainer}
                changeVisitsView={changeVisitsView}
                selectedOption={selectedOption}
            />
        );
    };

    onSortToggle = () => {
        const { sortPopupOpen } = this.state;
        this.setState({
            sortPopupOpen: !sortPopupOpen
        });
    };

    closeSortPopup = () => {
        this.setState({
            sortPopupOpen: false
        });
    };

    onFilterToggle = () => {
        const { filterPopupOpen } = this.state;
        this.setState({
            filterPopupOpen: !filterPopupOpen
        });
    };

    closeFilterPopup = () => {
        this.setState({
            filterPopupOpen: false
        });
    };

    onVisitClick = e => {
        e.preventDefault();
        e.stopPropagation();
        const { optionsVisible } = this.state;
        this.setState(
            {
                optionsVisible: !optionsVisible
            },
            () => {
                Tether.position();
            }
        );
    };

    onSearchChange = value => {
        const { onSearchInputChange } = this.props;
        onSearchInputChange(value);
        this.setState({
            searchValue: value
        });
    };

    onFilterOutsideClick = () => {
        this.setState({
            filterPopupOpen: false
        });
    };
    onSortOutsideClick = () => {
        this.setState({
            sortPopupOpen: false
        });
    };

    onClearInput = () => {
        const { onSearchInputChange } = this.props;
        onSearchInputChange(null);
        this.setState({
            searchValue: null
        });
    };

    render() {
        const {
            optionsVisible,
            selectedOption,
            filterPopupOpen,
            sortPopupOpen
        } = this.state;
        const {
            beats,
            pjPlan,
            applyFilters,
            paginationDetails,
            onPageChange,
            onPageSizeChange,
            sortBeats,
            resetSort,
            selectedSortOption,
            selectedSortOrder,
            outletSalesCategories,
            onClearFilter,
            selectedFilters,
            getAllFilters,
            t
        } = this.props;
        const filterCount = getAllFilters().length;
        return (
            <Box height="60px" className={theme.fpPlanViewActionBar} p={2}>
                <Columns alignY="center" space="xSmall">
                    <Column width="content">
                        <TetherComponent
                            attachment="top left"
                            targetAttachment="bottom left"
                            offset={'0 -3px'}
                            targetOffset={'0 0'}
                            constraints={[
                                {
                                    to: 'window',
                                    attachment: 'together'
                                }
                            ]}
                            optimizations={{
                                gpu: false
                            }}
                        >
                            <Flex p={2} height="fluid">
                                <Stack>
                                    <Box
                                        onClick={this.onVisitClick}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <Columns space="xSmall" alignY="center">
                                            <Column width="fluid">
                                                <Text
                                                    fontSize={2}
                                                    fontWeight="400"
                                                >
                                                    {selectedOption ===
                                                    FpContainers.Allocated
                                                        ? t(
                                                              'fpPlanView.allocated'
                                                          )
                                                        : t(
                                                              'fpPlanView.unallocated'
                                                          )}
                                                </Text>
                                            </Column>
                                            <Column width="content">
                                                <NeoIcon
                                                    size="medium"
                                                    icon="arrow_drop_down"
                                                />
                                            </Column>
                                        </Columns>
                                    </Box>
                                    {optionsVisible
                                        ? this.getPopoverOptions()
                                        : null}
                                </Stack>
                            </Flex>
                        </TetherComponent>
                    </Column>
                    <Column width="content">
                        <Divider
                            className={theme.divider}
                            orientation="vertical"
                            color={neoUIColors.grey600.code}
                        />
                    </Column>
                    <Column width="fluid">
                        <EnhancedInput
                            icon={<NeoIcon size="medium" icon="search" />}
                            className={theme.TourSearch}
                            theme={theme}
                            value={this.state.searchValue}
                            placeholder={t('fpPlanView.searchPlaceholder')}
                            onChange={this.onSearchChange}
                        />
                    </Column>

                    {selectedOption === FpContainers.Allocated ? (
                        <Column width="content">
                            <DivWithPopover
                                popOverContent={
                                    <FpBeatFilters
                                        t={t}
                                        outletSalesCategories={
                                            outletSalesCategories
                                        }
                                        getAllFilters={getAllFilters}
                                        onClearFilter={onClearFilter}
                                        applyFilters={applyFilters}
                                        pjPlan={pjPlan}
                                        beats={beats}
                                        closeFilterPopup={this.closeFilterPopup}
                                        filterPopupOpen={filterPopupOpen}
                                        theme={theme}
                                        selectedFilters={selectedFilters}
                                        handleClickOutside={
                                            this.onFilterOutsideClick
                                        }
                                    />
                                }
                                place={PopoverPlaceEnum.Below}
                                disableArrow
                                isPopOverOpen={filterPopupOpen}
                                invertPopover={false}
                                className={theme.fpBeatFilters}
                                popOverClassName={theme.fpBeatFilters}
                                theme={theme}
                            >
                                <Position position="relative">
                                    <NeoButton
                                        appearance="icon"
                                        icon="filter_active"
                                        onClick={this.onFilterToggle}
                                    />
                                    {filterCount ? (
                                        <Position
                                            position="absolute"
                                            backgroundColor="blue.500"
                                            top="-8px"
                                            right="-8px"
                                            sx={{
                                                borderRadius: '100%',
                                                border: '1px solid white'
                                            }}
                                        >
                                            <Box height="16px" width="18px">
                                                <Columns
                                                    height="fluid"
                                                    alignY="center"
                                                    align="center"
                                                >
                                                    <Text
                                                        color="white"
                                                        fontWeight={600}
                                                    >
                                                        {filterCount}
                                                    </Text>
                                                </Columns>
                                            </Box>
                                        </Position>
                                    ) : null}
                                </Position>
                            </DivWithPopover>
                        </Column>
                    ) : null}
                    {selectedOption === FpContainers.Allocated ? (
                        <Column width="content">
                            <DivWithPopover
                                popOverContent={
                                    <FpBeatSort
                                        t={t}
                                        handleClickOutside={
                                            this.onSortOutsideClick
                                        }
                                        sortBeats={sortBeats}
                                        selectedSortOrder={selectedSortOrder}
                                        selectedSortOption={selectedSortOption}
                                        closeSortPopup={this.closeSortPopup}
                                        resetSort={resetSort}
                                        theme={theme}
                                    />
                                }
                                place={PopoverPlaceEnum.Below}
                                disableArrow
                                isPopOverOpen={sortPopupOpen}
                                invertPopover={false}
                                className={theme.fpBeatFilters}
                                popOverClassName={theme.fpBeatFilters}
                                theme={theme}
                            >
                                <NeoButton
                                    appearance="icon"
                                    icon="sort_active"
                                    onClick={this.onSortToggle}
                                />
                            </DivWithPopover>
                        </Column>
                    ) : null}

                    <Column width="content">
                        <FpPlanViewPagination
                            selectedPageIndex={
                                paginationDetails.selectedPageIndex
                            }
                            totalPages={paginationDetails.totalPages}
                            onPageChange={onPageChange}
                            pageSize={paginationDetails.pageSize}
                            onPageSizeChange={onPageSizeChange}
                            totalItems={paginationDetails.totalItems}
                            pageSizeOptions={paginationDetails.pageSizeOptions}
                        />
                    </Column>
                </Columns>
            </Box>
        );
    }
}
